import {
    AmazonComponent,
    AmazonField,
    AmazonParsedResult,
    ExcelParseResult,
    useAmazonExcelParser
} from '@/composables/amazonExcelParser';
import { TreeType } from '@/components/Layers/modal/types';
import { CanvaFont, ImportLayersElements, LayerElementsMetadata } from '@/typings/layers/layers';
import { IFileParserStrategy } from '@/typings/parsers/externalFileParser';
import { FileParserType } from '@/typings/parsers/fileParserTypes';
import { useColor } from '@/composables/color';
import { ColorObject } from '@/typings/libraries';

interface StyleContext {
    fontOptions?: CanvaFont[];
    colorOptions?: ColorObject[];
}

interface ColumnNode {
    kind: 'component' | 'canvasObject';
    node: AmazonComponent;
}

export class AmazonExcelFileParser implements IFileParserStrategy<AmazonParsedResult> {
    type: FileParserType = 'AMAZON_FILE_XLSX';
    page = 1;
    // Amazon positions/sizes are pixels on a 400x400 preview grid (template's "Data Definitions" sheet)
    private readonly AMAZON_GRID_SIZE = 400;
    // Amazon has no physical size, so the grid is upscaled to a printable page
    private readonly PAGE_SIZE_PX = 2400;
    private readonly color = useColor();

    async parse(buffer: ArrayBuffer): Promise<AmazonParsedResult> {
        const { parseExcelToJson, parseAmazonFormat } = useAmazonExcelParser();
        const result: ExcelParseResult<Record<string, unknown>> = await parseExcelToJson(buffer, {
            sheetIndex: 1
        });
        return parseAmazonFormat(result.data, result.worksheet);
    }

    async extractLayers(result: AmazonParsedResult, importLayers: ImportLayersElements): Promise<ImportLayersElements> {
        const selectedPage = importLayers?.selectedPage || this.page;

        return {
            ...importLayers,
            type: this.type,
            mode: 'LAYERS',
            artWorkThumbnail: importLayers?.artWorkThumbnail || '',
            artworkName: (result.template.name as string) || 'Amazon Custom Template',
            includeDimensions: importLayers?.includeDimensions ?? true,
            selectedPage,
            pages: [
                {
                    pageNumber: 1,
                    name: result.surfaces[0]?.type || 'Page 1',
                    artWorkWidth: this.PAGE_SIZE_PX,
                    artWorkHeight: this.PAGE_SIZE_PX,
                    elements: this.buildElements(result)
                }
            ]
        };
    }

    /**
     * Columns follow Amazon's template tree depth-first, so the Font/ColorChooser columns of a
     * text group always precede that group's placement: walking them in column order and carrying
     * the last seen choosers as context attaches each text to its own fonts/colors.
     */
    private buildElements(result: AmazonParsedResult): LayerElementsMetadata[] {
        const columns: ColumnNode[] = [
            ...result.components.map((node): ColumnNode => ({ kind: 'component', node })),
            ...result.canvasObjects.map((node): ColumnNode => ({ kind: 'canvasObject', node }))
        ].sort((a, b) => (a.node.columnIndex ?? 0) - (b.node.columnIndex ?? 0));

        const elements: LayerElementsMetadata[] = [];
        let context: StyleContext = {};

        for (const column of columns) {
            if (column.kind === 'component') {
                context = this.mergeStyleContext(context, column.node);
                continue;
            }

            const binding = result.bindings.find((b) => b.canvasObjectId === column.node.id);
            const leaf = binding ? result.components.find((c) => c.id === binding.componentId) : undefined;
            const element = this.buildElement(column.node, leaf, context, elements.length);
            if (element) elements.push(element);
        }

        return elements;
    }

    private mergeStyleContext(context: StyleContext, component: AmazonComponent): StyleContext {
        const type = component.type.toLowerCase();
        if (type.includes('fontchooser')) {
            const fontOptions = this.extractFontOptions(component);
            return fontOptions.length ? { ...context, fontOptions } : context;
        }
        if (type.includes('colorchooser')) {
            const colorOptions = this.extractColorOptions(component);
            return colorOptions.length ? { ...context, colorOptions } : context;
        }
        return context;
    }

    private extractFontOptions(component: AmazonComponent): CanvaFont[] {
        return (component.children || [])
            .map((option) => {
                const family = this.fieldText(option.fields, 'family');
                if (!family) return null;
                const fileUrl = this.fieldText(option.fields, 'files.truetype.url');
                return { family, style: 'Regular', rawTypeface: family, ...(fileUrl && { fileUrl }) };
            })
            .filter((font): font is CanvaFont => font !== null);
    }

    private extractColorOptions(component: AmazonComponent): ColorObject[] {
        return (component.children || [])
            .map((option) => this.fieldText(option.fields, 'value'))
            .filter((value): value is string => !!value)
            .map((value) => this.toColorObject(value));
    }

    private toColorObject(value: string): ColorObject {
        const hexValue = value.startsWith('#') ? value : `#${value}`;
        const { cmyk, hex, a } = this.color.getColorFromString(hexValue);
        return { hex, alpha: a, cmyk: this.color.cmykToDecimalString(cmyk) };
    }

    private buildElement(
        canvasObject: AmazonComponent,
        leaf: AmazonComponent | undefined,
        context: StyleContext,
        zIndex: number
    ): LayerElementsMetadata | null {
        const width = this.fieldNumber(canvasObject.fields, 'dimension.width');
        const height = this.fieldNumber(canvasObject.fields, 'dimension.height');
        // Blank placement cells mean the SKU opted out of this customization
        if (width === undefined || height === undefined) return null;

        const scale = this.PAGE_SIZE_PX / this.AMAZON_GRID_SIZE;
        const isImage = leaf ? leaf.type === 'ImageInputComponent' : canvasObject.type.toLowerCase().includes('image');
        const name = canvasObject.type;
        const placeholder = leaf && this.fieldText(leaf.fields, 'placeholder');
        const label = leaf && this.fieldText(leaf.fields, 'label');
        const maxLines = Math.max(1, (leaf && this.fieldNumber(leaf.fields, 'maxLines')) || 1);
        const scaledHeight = height * scale;

        return {
            width: width * scale,
            height: scaledHeight,
            // Top-left: the import consumer (createStepOneCtrl.js) derives the center itself
            left: (this.fieldNumber(canvasObject.fields, 'position.x') ?? 0) * scale,
            top: (this.fieldNumber(canvasObject.fields, 'position.y') ?? 0) * scale,
            rotation: this.fieldNumber(canvasObject.fields, 'rotation.angleOfRotation') ?? 0,
            visible: true,
            name,
            textValue: isImage ? name : placeholder || label || name,
            // Excel export has no image data (buyer uploads it), so images must be placeholders
            type: isImage ? TreeType.FROM_FILE_IMAGE_PLACEHOLDER_ELEMENT : TreeType.FROM_FILE_TEXT_ELEMENT,
            // Amazon sizes the font to the full line height (matched against its live preview)
            fontSize: Math.round(scaledHeight / maxLines),
            multiline: maxLines > 1,
            ...(!isImage && {
                font: context.fontOptions?.[0],
                fontOptions: context.fontOptions,
                colorFill: context.colorOptions?.[0],
                colorOptions: context.colorOptions
            }),
            zIndex
        };
    }

    private fieldValue(fields: AmazonField[], fieldName: string): unknown {
        return fields.find((f) => f.field === fieldName)?.value;
    }

    private fieldNumber(fields: AmazonField[], fieldName: string): number | undefined {
        const value = this.fieldValue(fields, fieldName);
        const number = typeof value === 'number' ? value : Number(this.cellText(value));
        return value === null || value === undefined || value === '' || Number.isNaN(number) ? undefined : number;
    }

    private fieldText(fields: AmazonField[], fieldName: string): string | undefined {
        return this.cellText(this.fieldValue(fields, fieldName)) || undefined;
    }

    // ExcelJS returns rich text and hyperlink cells (e.g. font URLs) as objects
    private cellText(value: unknown): string {
        if (value === null || value === undefined) return '';
        if (typeof value !== 'object') return String(value).trim();
        const cell = value as { text?: unknown; richText?: { text: string }[] };
        if (Array.isArray(cell.richText))
            return cell.richText
                .map((part) => part.text)
                .join('')
                .trim();
        return typeof cell.text === 'string' ? cell.text.trim() : '';
    }
}
